import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useLogin } from '../../application/useLogin';
import AuthCardLayout from './AuthCardLayout';
import PasswordField from './PasswordField';

/** UC-1: usa useLogin (T24). Enlace hardcodeado (C-1 preservado). */
export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/dashboard';

  const { error, loading, submit, clearError } = useLogin();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = await submit(username, password);
    if (result) {
      navigate(result.forcePasswordChange ? '/change-password' : from, { replace: true });
    }
  };

  return (
    <AuthCardLayout>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
        {t('auth.platformSubtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('auth.username')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          margin="normal"
          autoComplete="username"
          disabled={loading}
        />
        <PasswordField
          fullWidth
          label={t('auth.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          autoComplete="current-password"
          disabled={loading}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
          sx={{ mt: 3, py: 1.5 }}
        >
          {loading ? t('auth.loggingIn') : t('auth.login')}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            component={Link}
            to="/forgot-password"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            ¿Olvidaste tu contraseña?
          </Button>
        </Box>
      </form>
    </AuthCardLayout>
  );
}
